/**
 * LocalStorePort — device-local, app-private durable storage for PRODUCT state.
 *
 * This is the ports layer (ARCHITECTURE §3, CLAUDE.md §8). Feature code never
 * touches a storage API directly; it receives a port, and the port is chosen
 * once by `selectLocalStore()`.
 *
 * WHY A SECOND STORAGE PORT, NEXT TO `session-storage.ts`:
 *
 *   `SessionStoragePort` exists for AUTH MATERIAL, and D-005 constrains it
 *   hard — on native it must be the platform secure store, and "we could not
 *   tell what platform this is" must not persist at all. That port's rules are
 *   the right rules for a refresh token and the wrong rules for a Capture
 *   draft: a draft that silently refused to persist on an undetermined
 *   platform would lose Brandon's words, which is the exact failure M4 exists
 *   to remove.
 *
 *   So the two are deliberately separate. **This port never holds auth
 *   material** — no token, no refresh token, no session, no password — and
 *   nothing in `src/data/drafts/` writes one. That is asserted by test, not
 *   just stated here.
 *
 * WHY IndexedDB (M4 / S-04-01, the authorized mechanism):
 *
 *   * present in every runtime Crewlink ships into — the browser and the
 *     Chromium WebView that Capacitor Android runs, and WKWebView on iOS
 *   * app-private: it lives in the app container, so it goes when the app goes
 *   * structured values, so the draft envelope round-trips without a
 *     hand-rolled serialization format
 *   * **no new dependency** — it is a platform API, not a package
 *
 *   The same implementation serves web and Capacitor, because in both cases
 *   the runtime IS a WebView with IndexedDB. There is no `@capacitor/*` import
 *   here and none is needed; if a future slice ever needs a native filesystem
 *   store, it replaces this adapter behind the same interface.
 *
 * WHY IT SURVIVES ABRUPT PROCESS DEATH: every write resolves on the
 * transaction's `complete` event, which is the point at which IndexedDB has
 * committed. A caller that awaits `set()` therefore knows the value is on disk,
 * not merely queued — which is what lets the Capture screen persist an
 * ambiguous Save's frozen payload BEFORE the request leaves the device. Nothing
 * here depends on a graceful shutdown, an unload handler, or the OS giving the
 * app a chance to clean up: those are exactly what an Android low-memory kill
 * does not provide.
 *
 * DEGRADED IS SILENT AND SAFE. If the database cannot be opened at all —
 * storage disabled, private mode, a quota refusal — every operation becomes a
 * no-op and `get` answers `null`. The product loses recovery, never
 * correctness: a draft that cannot be written is simply never offered back.
 * The failure is reported once through `reportError` so it is diagnosable
 * rather than invisible.
 */

import { reportError } from '../lib/monitoring';

export interface LocalStorePort {
  /** Human-readable adapter name. Diagnostics only, never product UI. */
  readonly name: string;
  /**
   * Whether this adapter survives process death. `false` means the runtime
   * offered no durable store and recovery is not available in it — stated so a
   * caller can never mistake an in-memory fallback for real persistence.
   */
  readonly durable: boolean;
  /** The stored value, or `null` when absent or unreadable. Never throws. */
  get(key: string): Promise<unknown>;
  /** Resolves once the value is COMMITTED, not merely queued. Never throws. */
  set(key: string, value: unknown): Promise<void>;
  /** Resolves once the removal is committed. Never throws. */
  remove(key: string): Promise<void>;
  /**
   * Every stored key beginning with `prefix` (M5 / S05-03 — the delivery queue
   * must enumerate an owner's staged photos to reconstruct itself after a
   * restart). Optional so pre-existing fakes keep compiling; every adapter in
   * this file implements it. Returns [] when unsupported or degraded — an
   * empty queue, never a crash.
   */
  keys?(prefix: string): Promise<string[]>;
}

/**
 * One Crewlink database, TWO object stores. Named constants so tests can assert
 * them.
 *
 * **VERSION 2 — M4 / S-04-02.** Version 1 shipped with `records` alone. The
 * audio store is ADDED beside it, never in place of it: the upgrade handler
 * creates only what is missing and touches no existing store, so a phone
 * carrying an unfinished typed Capture from S-04-01 keeps it across the
 * upgrade. Deleting and recreating `records` on upgrade would be the quiet
 * version of losing Brandon's words, and a test asserts it does not happen.
 *
 * **Two stores rather than two databases**, because one `onupgradeneeded` that
 * knows about both is far easier to keep correct than two schemas that must
 * agree about a version number they cannot see.
 */
export const LOCAL_STORE_DB_NAME = 'crewlink-local';
export const LOCAL_STORE_DB_VERSION = 3;
export const LOCAL_STORE_NAMESPACE = 'records';
/** Temporary Capture audio (S-04-02). Blobs only ever live here. */
export const LOCAL_STORE_AUDIO_NAMESPACE = 'audio';
/**
 * Staged verified-clean photos awaiting delivery (M5 / S05-03). Version 3 adds
 * this store; the additive upgrade below means a phone carrying an unfinished
 * draft or a recoverable recording from v2 keeps both across the upgrade.
 */
export const LOCAL_STORE_PHOTOS_NAMESPACE = 'photos';
/** Every store this database version owns. The upgrade creates each if absent. */
export const LOCAL_STORE_NAMESPACES = [
  LOCAL_STORE_NAMESPACE,
  LOCAL_STORE_AUDIO_NAMESPACE,
  LOCAL_STORE_PHOTOS_NAMESPACE,
] as const;

/**
 * In-memory adapter — for runtimes with no IndexedDB (jsdom, SSR) and for
 * tests.
 *
 * `backing` is injectable ON PURPOSE: handing the SAME map to two store
 * instances is how a test simulates a process restart — the first instance
 * writes, is thrown away, and a second instance reads what survived. Without
 * that seam, "survives a new process" could only be asserted against a mock of
 * itself.
 *
 * Values are JSON round-tripped rather than stored by reference, so a caller
 * cannot mutate a "persisted" record through the object it handed in. That
 * mirrors what a real store does and keeps the fake honest.
 */
export function createMemoryLocalStore(
  name = 'memory',
  backing: Map<string, string> = new Map(),
): LocalStorePort {
  return {
    name,
    durable: false,
    get(key) {
      const raw = backing.get(key);
      if (raw === undefined) return Promise.resolve(null);
      try {
        return Promise.resolve(JSON.parse(raw) as unknown);
      } catch {
        return Promise.resolve(null);
      }
    },
    set(key, value) {
      backing.set(key, JSON.stringify(value));
      return Promise.resolve();
    },
    remove(key) {
      backing.delete(key);
      return Promise.resolve();
    },
    keys(prefix) {
      return Promise.resolve([...backing.keys()].filter((k) => k.startsWith(prefix)));
    },
  };
}

/**
 * BLOB-SAFE in-memory adapter — M4 / S-04-02.
 *
 * **A Blob does not survive `JSON.stringify`.** It becomes `{}`, silently, with
 * no error anywhere — so routing audio through `createMemoryLocalStore` would
 * "succeed" and hand back an empty object later, which is exactly the shape of
 * a fake recovery. This adapter keeps the value BY REFERENCE instead.
 *
 * That reference-keeping is not carelessness, it is the only correct choice
 * here: structured data can be copied by serialising it, a Blob cannot, and a
 * fallback that quietly destroyed the audio it was asked to hold would be
 * worse than one that admits it is not durable. It says `durable: false`, and
 * the product uses that to tell Brandon the recording will not survive closing
 * Crewlink rather than pretending it will.
 */
export function createMemoryBlobStore(
  name = 'memory:blob',
  backing: Map<string, unknown> = new Map(),
): LocalStorePort {
  return {
    name,
    durable: false,
    get: (key) => Promise.resolve(backing.has(key) ? backing.get(key) : null),
    set: (key, value) => {
      backing.set(key, value);
      return Promise.resolve();
    },
    remove: (key) => {
      backing.delete(key);
      return Promise.resolve();
    },
    keys: (prefix) => Promise.resolve([...backing.keys()].filter((k) => k.startsWith(prefix))),
  };
}

/**
 * The real adapter — IndexedDB, behind ONE readiness gate.
 *
 * The gate is the same shape the secure session store uses and for the same
 * reason: opening a database is asynchronous, callers are not required to know
 * that, and every operation must queue behind the open rather than race it.
 * The gate NEVER REJECTS — `.catch` is attached where it is created, so no
 * unobserved rejection can escape — it sets degraded mode instead.
 *
 * `factory` is a parameter rather than the ambient global so tests can drive a
 * fake IndexedDB through this exact code path. jsdom ships no IndexedDB at all,
 * so without the seam this adapter could only ever be tested on a real device.
 */
export function createIndexedDbLocalStore(
  factory: IDBFactory,
  /**
   * Which object store this port speaks to. `records` for the JSON draft
   * envelope, `audio` for Blobs — the same database, the same upgrade, two
   * separate namespaces so an audio record can never be mistaken for a draft.
   */
  namespace: string = LOCAL_STORE_NAMESPACE,
): LocalStorePort {
  let degraded = false;
  let database: IDBDatabase | null = null;

  const ready: Promise<void> = new Promise<void>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(LOCAL_STORE_DB_NAME, LOCAL_STORE_DB_VERSION);
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
      return;
    }

    /**
     * ADDITIVE ONLY, AND DELIBERATELY SO.
     *
     * Whichever port triggers the upgrade creates EVERY store this version
     * owns — so opening the audio port on a phone that has only ever had
     * `records` upgrades the database correctly, and vice versa. `contains`
     * guards each creation, and nothing is ever deleted or recreated: an
     * unfinished Capture written by S-04-01 survives the S-04-02 upgrade
     * untouched. There is no `deleteObjectStore` in this file, on purpose.
     */
    request.onupgradeneeded = () => {
      const opened = request.result;
      for (const store of LOCAL_STORE_NAMESPACES) {
        if (!opened.objectStoreNames.contains(store)) {
          opened.createObjectStore(store);
        }
      }
    };
    request.onsuccess = () => {
      database = request.result;
      resolve();
    };
    request.onerror = () => {
      reject(request.error ?? new Error('indexedDB.open failed'));
    };
    // A blocked open means another tab holds an older version. Recovery is a
    // convenience; refusing to hang is worth more than waiting for that tab.
    request.onblocked = () => {
      reject(new Error('indexedDB.open blocked by another connection'));
    };
  }).catch((error: unknown) => {
    degraded = true;
    reportError(error, `localStore.${namespace}.open`);
  });

  /**
   * One transaction, resolved on `complete` — the commit point.
   *
   * Resolving on the REQUEST's success instead would be the subtle version of
   * this bug: the request succeeds while the transaction is still open, so a
   * caller awaiting it could dispatch a network request believing a value was
   * on disk that a process kill would then lose. The whole durability claim in
   * this file rests on this one choice.
   */
  async function run<T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore) => IDBRequest<T>,
    context: string,
  ): Promise<T | null> {
    await ready;
    const opened = database;
    if (degraded || !opened) return null;

    return new Promise<T | null>((resolve) => {
      let result: T | null = null;
      let transaction: IDBTransaction;
      try {
        transaction = opened.transaction(namespace, mode);
      } catch (error) {
        reportError(error, `localStore.${context}`);
        resolve(null);
        return;
      }

      try {
        const request = body(transaction.objectStore(namespace));
        request.onsuccess = () => {
          result = request.result;
        };
      } catch (error) {
        reportError(error, `localStore.${context}`);
        resolve(null);
        return;
      }

      transaction.oncomplete = () => {
        resolve(result);
      };
      transaction.onerror = () => {
        reportError(transaction.error, `localStore.${context}`);
        resolve(null);
      };
      transaction.onabort = () => {
        reportError(transaction.error, `localStore.${context}.abort`);
        resolve(null);
      };
    });
  }

  return {
    name: `indexeddb:${namespace}`,
    /**
     * **A GETTER, NOT A LITERAL — M4 / S-04-03, closing I-036 narrowly.**
     *
     * This adapter used to report `durable: true` forever, decided at
     * construction and never revisited. But the gate above can FAIL — storage
     * disabled, private mode, a quota refusal, a blocked open — and after that
     * every operation is a silent no-op. The adapter went on claiming
     * durability it no longer had, and the product had no way to tell Brandon
     * that his unfinished work would not survive closing the app.
     *
     * Reading `degraded` at the moment the question is asked is the whole fix.
     * It is deliberately NOT a repair: nothing here reopens the database,
     * retries, or asks for persistent-storage quota. Those belong to a wider
     * slice that has not been authorized. This makes the honest answer
     * available; what the product SAYS with it is the Capture screen's job.
     */
    get durable() {
      return !degraded;
    },
    async get(key) {
      // IndexedDB answers `undefined` for an absent key. The port promises ONE
      // "there is nothing here" value, so it is normalised at the boundary
      // rather than left for every caller to remember separately.
      const value = await run<unknown>(
        'readonly',
        (store) => store.get(key) as IDBRequest<unknown>,
        'get',
      );
      return value ?? null;
    },
    async set(key, value) {
      await run<IDBValidKey>('readwrite', (store) => store.put(value, key), 'set');
    },
    async remove(key) {
      await run<undefined>('readwrite', (store) => store.delete(key), 'remove');
    },
    async keys(prefix) {
      // getAllKeys() without a range, filtered here — a key range would be
      // marginally cheaper, but IDBKeyRange does not exist in the jsdom test
      // runtime and this store holds at most a handful of staged photos.
      // Degraded mode answers [] through `run`'s null — an empty queue, never
      // a crash.
      const found = await run<IDBValidKey[]>('readonly', (store) => store.getAllKeys(), 'keys');
      return (found ?? []).filter(
        (k): k is string => typeof k === 'string' && k.startsWith(prefix),
      );
    },
  };
}

/**
 * The runtime's IndexedDB, or `null` when it has none.
 *
 * Wrapped because merely READING the property can throw: a browser with site
 * data disabled raises a SecurityError on access rather than reporting absence.
 * "Cannot tell" and "not there" both answer `null` here, so callers have one
 * case to handle.
 */
export function detectIndexedDb(): IDBFactory | null {
  try {
    return (globalThis as { indexedDB?: IDBFactory }).indexedDB ?? null;
  } catch {
    return null;
  }
}

/**
 * Chosen once, at first use.
 *
 * There is no platform branch here, and that is deliberate rather than an
 * omission: web and Capacitor both run a WebView with IndexedDB, so one
 * implementation is honest for both. The fallback is a NON-DURABLE memory
 * store that says so in `durable` — it never pretends to persist.
 */
export function selectLocalStore(): LocalStorePort {
  const factory = detectIndexedDb();
  if (!factory) return createMemoryLocalStore('memory:no-indexeddb');
  return createIndexedDbLocalStore(factory, LOCAL_STORE_NAMESPACE);
}

/**
 * The temporary-audio store — M4 / S-04-02.
 *
 * Same database, the `audio` object store, and a **Blob-safe** fallback rather
 * than the JSON one. The fallback still reports `durable: false`, so a runtime
 * without IndexedDB gets audio that works for this session and says so instead
 * of a recording that claims to have been kept and was not.
 */
export function selectAudioStore(): LocalStorePort {
  const factory = detectIndexedDb();
  if (!factory) return createMemoryBlobStore('memory:no-indexeddb');
  return createIndexedDbLocalStore(factory, LOCAL_STORE_AUDIO_NAMESPACE);
}

/**
 * The staged-photo store — M5 / S05-03.
 *
 * Same database, the `photos` object store. A staged record carries binary
 * variants (archive, display, thumbnail), so the fallback is the Blob-safe
 * memory store — same reasoning as audio: a runtime without IndexedDB gets
 * photos that work for this session and SAY they are not durable, never a
 * silent JSON mangling of binary data.
 */
export function selectPhotoStagingBackingStore(): LocalStorePort {
  const factory = detectIndexedDb();
  if (!factory) return createMemoryBlobStore('memory:no-indexeddb');
  return createIndexedDbLocalStore(factory, LOCAL_STORE_PHOTOS_NAMESPACE);
}
